import React, { useState, useEffect } from 'react';
import { Award, Users, FlaskConical, Clock, ImageIcon } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { publicAPI } from '../services/api';

// Placeholder component for empty gallery slots - defined outside component to avoid re-creation
const ImagePlaceholder = ({ title }) => (
  <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
    <ImageIcon className="w-12 h-12 text-gray-300 mb-2" />
    <span className="text-xs text-gray-400 text-center px-2">{title || 'Gallery Image'}</span>
  </div>
);

// Optimize image URLs for faster loading
const optimizeImageUrl = (url, width = 400) => {
  if (!url) return url;
  // For Unsplash images, add optimization parameters
  if (url.includes('unsplash.com')) {
    const baseUrl = url.split('?')[0];
    return `${baseUrl}?w=${width}&q=75&auto=format&fit=crop`;
  }
  // For Pexels images
  if (url.includes('pexels.com')) {
    return url.includes('?') ? `${url}&w=${width}` : `${url}?w=${width}`;
  }
  return url;
};

// Gallery image component with safe error handling
const GalleryImage = ({ image, title, className }) => {
  const [hasError, setHasError] = useState(false);
  
  if (!image || hasError) {
    return <ImagePlaceholder title={title} />;
  }
  
  return (
    <img
      src={optimizeImageUrl(image, 400)}
      alt={title || "Gallery Image"}
      className={`w-full h-full object-cover hover:scale-110 transition-transform duration-500 ${className || ''}`}
      loading="lazy"
      onError={() => setHasError(true)}
    />
  );
};

const AboutSection = () => {
  const [galleryImages, setGalleryImages] = useState([]);

  // Fetch gallery images from API
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await publicAPI.getGallery();
        if (response.data && response.data.length > 0) {
          // Get first 4 images that have valid image URLs
          const validImages = response.data.filter(img => img.image && img.image.trim() !== '');
          setGalleryImages(validImages.slice(0, 4));
        }
      } catch (error) {
        console.error('Failed to fetch gallery:', error);
      }
    };
    fetchGallery();
  }, []);

  const highlights = [
    {
      icon: Award,
      value: '32+',
      label: 'Years of Trust'
    },
    {
      icon: Users,
      value: '100k+',
      label: 'Happy Patients'
    },
    {
      icon: FlaskConical,
      value: '1000k+',
      label: 'Tests Conducted'
    },
    {
      icon: Clock,
      value: '50+',
      label: 'Specialists'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image Grid - First 4 Gallery Images */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden h-48 bg-gray-100">
                  <GalleryImage 
                    image={galleryImages[0]?.image} 
                    title={galleryImages[0]?.title || "Reception Area"} 
                  />
                </div>
                <div className="rounded-2xl overflow-hidden h-64 bg-gray-100">
                  <GalleryImage 
                    image={galleryImages[1]?.image} 
                    title={galleryImages[1]?.title || "Waiting Lounge"} 
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden h-64 bg-gray-100">
                  <GalleryImage 
                    image={galleryImages[2]?.image} 
                    title={galleryImages[2]?.title || "Patient Area"} 
                  />
                </div>
                <div className="rounded-2xl overflow-hidden h-48 bg-gray-100">
                  <GalleryImage 
                    image={galleryImages[3]?.image} 
                    title={galleryImages[3]?.title || "Consultation Room"} 
                  />
                </div>
              </div>
            </div>

            {/* Floating Badge */}
            <div className="absolute -bottom-6 -right-6 bg-[#007897] text-white p-6 rounded-2xl shadow-xl hidden md:block">
              <div className="text-4xl font-bold">32+</div>
              <div className="text-blue-200 text-sm">Years of Trust</div>
            </div>
          </div>

          {/* Content */}
          <div>
            <span className="text-[#007897] font-semibold text-sm uppercase tracking-wider">About Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6">
              Welcome to Nanda Diagnostics
            </h2>
            
            <p className="text-gray-600 mb-6 leading-relaxed">
              {companyInfo.description}
            </p>
            
            <p className="text-gray-600 mb-8 leading-relaxed">
              At Nanda Diagnostics, our journey is a testament to our dedication to staying ahead in the healthcare industry. We continuously update our methodologies and equipment to ensure that we provide the most accurate and reliable diagnostic services available.
            </p>

            {/* Highlights Grid */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              {highlights.map((item, index) => (
                <div key={`stat-${index}`} className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#007897]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-[#007897]" />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-gray-900">{item.value}</div>
                    <div className="text-sm text-gray-600">{item.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
